import {
  CohortResponseDuration,
  CohortResponseEndDate,
  CohortResponseStartDate,
  CohortResponseTimeCommitment,
  CohortType,
  SAPApplicationParameters,
} from '@platform/client/schemas';
import { Button, Logo } from '@platform/components';
import { ApplicationStorageDataKeys } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { AuthedPage } from '../../../app';
import { ROUTES } from '../../../routes/consts';
import { navigate } from 'wouter/use-browser-location';
import { Application } from '@platform/client';
import Skeleton from 'react-loading-skeleton';

const dateOptions = {
  month: 'short',
  day: 'numeric',
} as Intl.DateTimeFormatOptions;

export const SapCohortSelectionPage = () => {
  const { updateApplicationData } = useApplicationLocalStorage<SAPApplicationParameters>(
    ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION,
  );
  const { data: cohortsData, isSuccess, isLoading } = Application.useGetAvailableCohortsSap();
  return (
    <AuthedPage>
      <div className='flex flex-col overflow-x-clip overflow-y-auto h-[100vh] bg-gray-50'>
        <div className='flex-grow w-full py-2 px-3 text-gray-600'>
          <div className='w-full p-4'>
            <Logo type='mw-text' variant='blue' />
          </div>
          <div className='w-full md:w-[700px] flex flex-col gap-8 mx-auto'>
            <div>
              <h2 className='text-display-sm font-medium mb-4'>
                Choose how you want to participate
              </h2>
              <h4 className='text-md'>
                Choose the cohort that fits your availability, skills, and interests.
              </h4>
            </div>
            {isLoading && <Skeleton height={200} width={'100%'} className='mb-4' />}
            {isLoading && <Skeleton height={200} width={'100%'} className='mb-4' />}
            {isSuccess &&
              cohortsData.length > 0 &&
              cohortsData.map((cohort) => (
                <CohortCard
                  key={cohort.cohortId}
                  name={cohort.displayName}
                  supportText={cohort.description}
                  startDate={cohort.startDate}
                  endDate={cohort.endDate}
                  durationText={cohort.duration}
                  timeCommitmentText={cohort.timeCommitment}
                  isTeam={cohort.type === CohortType.Teams}
                  onClick={() => {
                    updateApplicationData({
                      cohort: cohort.cohortId,
                      teamSelection: cohort.type === CohortType.Teams ? 'team' : 'individual',
                    });
                    navigate(`/platform/auth${ROUTES.APPLICATION_START}?partner=sap`);
                  }}
                />
              ))}
            {isSuccess && cohortsData.length === 0 && (
              // TODO: update this to re-usable component when applying to other programs on new stack
              <div className='w-full p-10 bg-white rounded-xl border border-gray-100 text-center'>
                <h3 className='text-lg font-medium'>
                  This SAP Acceleration Collective application period is closed
                </h3>
                <p className='text-sm text-gray-500 mt-2'>
                  Please check back later for updates or&nbsp;
                  <SupportLink />
                  &nbsp;for more information.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthedPage>
  );
};

const SupportLink = () => {
  return (
    <a className='blue-link' href='mailto:support@movingworlds.org'>
      contact support
    </a>
  );
};

const CohortCard = ({
  name,
  supportText,
  startDate,
  endDate,
  durationText,
  timeCommitmentText,
  isTeam,
  onClick,
}: {
  name: string;
  supportText: string;
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
  durationText: CohortResponseDuration;
  timeCommitmentText: CohortResponseTimeCommitment;
  isTeam: boolean;
  onClick: () => void;
}) => {
  return (
    <div className='bg-white flex flex-col gap-6 shadow-xs rounded-xl border border-gray-100 p-6 w-full'>
      <div>
        <div className='flex gap-2 items-center justify-between'>
          <h3 className='text-xl font-medium'>{name}</h3>
        </div>
        <p className='text-sm text-gray-500'>{supportText}</p>
      </div>
      <CohortDetailsSection
        startDate={startDate}
        endDate={endDate}
        durationText={durationText}
        timeCommitmentText={timeCommitmentText}
        isTeam={isTeam}
      />
      <Button variant='primary' size='sm' additionalClassnames='w-fit' onPress={onClick}>
        Apply to this cohort
      </Button>
    </div>
  );
};

const CohortDetailsSection = ({
  startDate,
  endDate,
  durationText,
  timeCommitmentText,
  isTeam,
}: {
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
  durationText: CohortResponseDuration;
  timeCommitmentText: CohortResponseTimeCommitment;
  isTeam: boolean;
}) => {
  return (
    <div className='flex w-full gap-20'>
      <CohortDatesSection startDate={startDate} endDate={endDate} />
      <CohortDetail label='Duration' value={durationText} />
      <CohortDetail label='Time Commitment' value={timeCommitmentText} />
      <CohortDetail label='Format' value={isTeam ? 'Team' : 'Individual'} />
    </div>
  );
};

const CohortDatesSection = ({
  startDate,
  endDate,
}: {
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
}) => {
  if (!startDate || !endDate) return null;
  const datesText = `${new Date(startDate).toLocaleDateString('en-US', dateOptions)} - ${new Date(
    endDate,
  ).toLocaleDateString('en-US', dateOptions)}`;
  return <CohortDetail label='Dates' value={datesText} />;
};

const CohortDetail = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div className='flex flex-col gap-1'>
      <p className='text-sm text-gray-400'>{label}</p>
      <p className='text-sm text-gray-500'>{value}</p>
    </div>
  );
};
